import React, { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "src/components/ui/dialog";
import { Button } from "src/components/ui/button";
import { Input } from "src/components/ui/input";
import { Label } from "src/components/ui/label";
import { Textarea } from "src/components/ui/textarea";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "src/components/ui/select";
import { useToast } from "src/components/ui/toast";
import { scheduleSiteVisit } from "src/features/site_visits/api/siteVisitsApi";
import { fetchProjects, fetchMembers } from "src/features/projects/api/projectsApi";
import { fetchLeads } from "src/features/leads/api/leadsApi";

const DURATIONS = [30, 45, 60, 90, 120];

// Local datetime string for the <input type="datetime-local"> default: now + 1h.
function defaultSchedule() {
  const d = new Date(Date.now() + 60 * 60 * 1000);
  d.setMinutes(0, 0, 0);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function ScheduleVisitModal({ open, onOpenChange, lead, onScheduled }) {
  const { toast } = useToast();
  const [leads, setLeads] = useState([]);
  const [projects, setProjects] = useState([]);
  const [members, setMembers] = useState([]);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    leadId: lead ? String(lead.id) : "",
    projectId: lead?.project?.id ? String(lead.project.id) : "",
    hostUserId: "",
    scheduledAt: defaultSchedule(),
    durationMinutes: "60",
    notes: "",
  });

  const set = (key) => (value) => setForm((f) => ({ ...f, [key]: value }));

  useEffect(() => {
    if (!open) return;
    fetchProjects({ per_page: 100, active: true }).then((d) => setProjects(d.projects || [])).catch(() => {});
    fetchMembers().then((d) => setMembers(d.memberships || d.members || [])).catch(() => {});
    if (!lead) {
      fetchLeads({ per_page: 50 }).then((d) => setLeads(d.leads || [])).catch(() => {});
    }
    setForm((f) => ({
      ...f,
      leadId: lead ? String(lead.id) : f.leadId,
      projectId: lead?.project?.id ? String(lead.project.id) : f.projectId,
      scheduledAt: defaultSchedule(),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  async function submit() {
    if (!form.leadId) {
      toast({ variant: "destructive", title: "Lead required", description: "Pick a lead for this visit." });
      return;
    }
    if (!form.scheduledAt) {
      toast({ variant: "destructive", title: "Date required", description: "Pick a date and time." });
      return;
    }
    setSaving(true);
    try {
      const res = await scheduleSiteVisit({
        leadId: form.leadId,
        projectId: form.projectId || undefined,
        hostUserId: form.hostUserId || undefined,
        scheduledAt: new Date(form.scheduledAt).toISOString(),
        durationMinutes: Number(form.durationMinutes),
        notes: form.notes || undefined,
      });
      toast({ title: "Visit scheduled", description: "The site visit is on the calendar." });
      onScheduled?.(res);
      onOpenChange(false);
    } catch (error) {
      const errs = error.response?.data?.errors;
      toast({
        variant: "destructive",
        title: "Couldn't schedule",
        description: Array.isArray(errs) ? errs.join(", ") : "Something went wrong.",
      });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Schedule a site visit</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {lead ? (
            <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm">
              <span className="text-slate-400">Lead</span>{" "}
              <span className="font-medium text-slate-800">{lead.name}</span>
            </div>
          ) : (
            <div className="space-y-1.5">
              <Label>Lead</Label>
              <Select value={form.leadId} onValueChange={set("leadId")}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a lead" />
                </SelectTrigger>
                <SelectContent>
                  {leads.map((l) => (
                    <SelectItem key={l.id} value={String(l.id)}>
                      {l.name} {l.phone ? `· ${l.phone}` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Project</Label>
              <Select value={form.projectId} onValueChange={set("projectId")}>
                <SelectTrigger>
                  <SelectValue placeholder="Optional" />
                </SelectTrigger>
                <SelectContent>
                  {projects.map((p) => (
                    <SelectItem key={p.id} value={String(p.id)}>
                      {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label>Host</Label>
              <Select value={form.hostUserId} onValueChange={set("hostUserId")}>
                <SelectTrigger>
                  <SelectValue placeholder="Me" />
                </SelectTrigger>
                <SelectContent>
                  {members.map((m) => (
                    <SelectItem key={m.user?.id || m.id} value={String(m.user?.id || m.user_id)}>
                      {m.user?.name || m.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Date &amp; time</Label>
              <Input
                type="datetime-local"
                value={form.scheduledAt}
                onChange={(e) => set("scheduledAt")(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Duration</Label>
              <Select value={form.durationMinutes} onValueChange={set("durationMinutes")}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DURATIONS.map((d) => (
                    <SelectItem key={d} value={String(d)}>
                      {d} min
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Notes</Label>
            <Textarea
              rows={2}
              placeholder="Anything the host should know…"
              value={form.notes}
              onChange={(e) => set("notes")(e.target.value)}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={saving}>
            {saving && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
            Schedule
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
